import { useNode } from '@craftjs/core';
import css from '@emotion/css';
import styled from '@emotion/styled';
import { useLoadNonWebFont } from '@kizen/page-builder/hooks/useLoadNonWebFont';
import { useSanitizedHtml } from '@kizen/page-builder/hooks/useSanitizedHtml';
import {
  ButtonContainer,
  TextContainer,
} from '@kizen/page-builder/nodes/containers';
import {
  ButtonActions,
  SharedViewBuilderButton,
} from '@kizen/page-builder/nodes/Button';
import {
  BaseImage,
  LinkWrapper,
  NoImageIcon,
  ViewImageContainer,
} from '@kizen/page-builder/nodes/Image';
import {
  TextBase,
  useFontFamiliesFromText,
} from '@kizen/page-builder/nodes/Text';
import { useViewerContext } from '@kizen/page-builder/viewer';
import {
  Fragment,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { StaticBlockRoot } from '@kizen/page-builder/nodes/Root';
import { SectionContainer } from '@kizen/page-builder/nodes/containers';
import { Cell } from '@kizen/page-builder/nodes/Cell';
import { HomepageRow } from '@kizen/page-builder/nodes/Row';
import { Content } from '@kizen/page-builder/nodes/Content';
import { ViewHTMLBlock } from '@kizen/page-builder/nodes/HTMLBlock';
import { ViewDivider } from '@kizen/page-builder/nodes/Divider';
import { richTextStyle } from 'components/WYSIWYG/MarkdownEditor/styles';
import { PluginContext } from 'ts-components/Plugins/context';
import { INDICATOR_TYPE } from '@kizenapps/engine/communication';
import { findMatchingAction } from '@kizenapps/engine/util';

const StyledText = styled(TextBase)`
  ${richTextStyle}

  ${({ rootNodeFontSize = '14' }) => css`
    font-size: ${rootNodeFontSize}px;
  `}

  ${({ rootNodeFontFamily }) => css`
    font-family: ${rootNodeFontFamily};
  `}

  && div[data-merge-field-unescaped] p,
  && div[data-merge-field-unescaped] li
  && div[data-merge-field-unescaped] li::marker {
    font-size: unset;
    font-family: unset;
  }
`;

const Button = (props) => {
  const { action, scriptApiName, activityId, automationId, url, ...rest } =
    props;
  const {
    javascriptActions,
    entityId,
    objectId,
    handleScriptError,
    onLogActivityButtonClick,
    onStartAutomationButtonClick,
    textTransform,
  } = useViewerContext();

  const { plugins = {} } = useContext(PluginContext);

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId,
    onError: handleScriptError,
  });

  const { pending, indicator } = internalState;

  const jsAction = useMemo(() => {
    return findMatchingAction(javascriptActions, scriptApiName);
  }, [javascriptActions, scriptApiName]);

  const pluginApp = useMemo(() => {
    return jsAction?.pluginApp?.apiName;
  }, [jsAction]);

  const handleClick = useCallback(() => {
    if (action === ButtonActions.SCRIPT_EXECUTION) {
      if (jsAction.script) {
        const config = plugins[pluginApp]?.business_config ?? {};
        executeScript(
          jsAction.script,
          { ...config, pluginId: jsAction.pluginApp?.id },
          { plugin_api_name: pluginApp, script_id: jsAction.id }
        );
      }
    } else if (action === ButtonActions.LOG_ACTIVITY) {
      onLogActivityButtonClick(activityId);
    } else if (action === ButtonActions.START_AUTOMATION) {
      onStartAutomationButtonClick(automationId);
    }
  }, [
    action,
    jsAction,
    pluginApp,
    executeScript,
    onLogActivityButtonClick,
    activityId,
    onStartAutomationButtonClick,
    automationId,
    plugins,
  ]);

  const parsedUrl = useMemo(
    () =>
      url && typeof textTransform === 'function' ? textTransform(url) : url,
    [url, textTransform]
  );

  return (
    <ButtonContainer {...rest}>
      <SharedViewBuilderButton
        onClick={handleClick}
        action={action}
        {...rest}
        url={parsedUrl}
        disabled={
          pending || (action === ButtonActions.SCRIPT_EXECUTION && !jsAction)
        }
        loading={indicator === INDICATOR_TYPE.BUTTON}
      />
    </ButtonContainer>
  );
};

const Image = forwardRef((props, ref) => {
  const {
    nodeId,
    src,
    alt,
    link,
    unit,
    display,
    position,
    height,
    width,
    minHeight,
    onNoImageClick,
    clickable = false,
    size,
    naturalHeight,
    naturalWidth,
    action,
    scriptApiName,
    ...rest
  } = props;

  const isClickable =
    clickable ||
    (action === ButtonActions.URL && Boolean(link)) ||
    (action === ButtonActions.SCRIPT_EXECUTION && Boolean(scriptApiName));

  const viewerCtx = useViewerContext();
  const {
    javascriptActions,
    entityId,
    objectId,
    handleScriptError,
    textTransform,
  } = viewerCtx;

  const [imageError, setImageError] = useState(false);

  const { plugins } = useContext(PluginContext);

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId,
    onError: handleScriptError,
  });

  const { pending, indicator } = internalState;

  const parsedLink = useMemo(
    () =>
      link && typeof textTransform === 'function' ? textTransform(link) : link,
    [link, textTransform]
  );

  const ImageWrapper = link?.length
    ? ({ children }) => (
        <LinkWrapper
          href={parsedLink}
          position={position}
          target="_blank"
          rel="noreferrer"
        >
          {children}
        </LinkWrapper>
      )
    : Fragment;

  const jsAction = useMemo(() => {
    return findMatchingAction(javascriptActions, scriptApiName);
  }, [javascriptActions, scriptApiName]);

  const handleClick = () => {
    if (jsAction?.script) {
      const pluginApp = jsAction.pluginApp?.apiName;
      const config = plugins[pluginApp]?.business_config ?? {};
      executeScript(
        jsAction.script,
        { ...config },
        { plugin_api_name: pluginApp, script_id: jsAction.id }
      );
    }
  };

  useEffect(() => {
    setImageError(false);
  }, [src]);

  return (
    <ViewImageContainer
      ref={ref}
      display={display}
      position={position}
      minHeight={minHeight}
      {...rest}
    >
      <ImageWrapper>
        {imageError ? (
          <NoImageIcon
            isViewerCtx={!Array.isArray(viewerCtx)}
            height={naturalHeight}
            alt={alt}
            onClick={onNoImageClick}
          />
        ) : (
          <BaseImage
            onError={() => setImageError(true)}
            onClick={
              pending || indicator === INDICATOR_TYPE.BUTTON
                ? undefined
                : handleClick
            }
            src={src}
            alt={alt}
            unit={unit}
            height={height}
            width={width}
            size={size}
            naturalHeight={naturalHeight}
            naturalWidth={naturalWidth}
            clickable={
              isClickable && !pending && indicator !== INDICATOR_TYPE.BUTTON
            }
          />
        )}
      </ImageWrapper>
    </ViewImageContainer>
  );
});

const Text = (props) => {
  const {
    rootNode,
    textTransform,
    javascriptActions,
    entityId,
    objectId,
    handleScriptError,
  } = useViewerContext() ?? {};

  const clickHandlersRef = useRef(new Map());
  const rootNodeProps = rootNode?.props ?? {};
  const { editorText } = useNode((node) => {
    const editorText =
      typeof textTransform === 'function'
        ? textTransform(node.data.custom?.text)
        : node.data.custom?.text;
    return { editorText };
  });
  const sanitized = useSanitizedHtml(editorText, { ADD_ATTR: ['target'] });
  const fontFamilies = useFontFamiliesFromText(editorText);
  useLoadNonWebFont(fontFamilies);

  const { plugins } = useContext(PluginContext);

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId,
    onError: handleScriptError,
  });

  const { pending } = internalState;

  const refCallback = useCallback(
    (node) => {
      if (node) {
        for (const [elem, handler] of clickHandlersRef.current.entries()) {
          elem.removeEventListener('click', handler);
        }

        clickHandlersRef.current.clear();

        for (const link of node.querySelectorAll('[data-script-api-name]')) {
          const scriptApiName = link.getAttribute('data-script-api-name');

          const action = findMatchingAction(javascriptActions, scriptApiName);

          if (action) {
            const handler = () => {
              if (action.script && !pending) {
                const pluginApp = action.pluginApp?.apiName;
                const config = plugins[pluginApp]?.business_config ?? {};
                executeScript(
                  action.script,
                  { ...config },
                  { plugin_api_name: pluginApp, script_id: action.id }
                );
              }
            };
            link.addEventListener('click', handler);
            clickHandlersRef.current.set(link, handler);
          }
        }
      }
    },
    [javascriptActions, executeScript, pending, plugins]
  );

  useEffect(() => {
    const handlers = clickHandlersRef.current;

    return () => {
      for (const [elem, handler] of handlers.entries()) {
        elem.removeEventListener('click', handler);
      }
      handlers.clear();
    };
  }, []);

  return (
    <TextContainer linkColor={rootNodeProps.linkColor} {...props}>
      <StyledText
        ref={refCallback}
        {...props}
        rootNodeFontSize={rootNodeProps.fontSize}
        rootNodeFontFamily={rootNodeProps.fontFamily}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      ></StyledText>
    </TextContainer>
  );
};

export default {
  Button,
  Cell,
  Content,
  Divider: ViewDivider,
  HTMLBlock: ViewHTMLBlock,
  Image,
  Root: StaticBlockRoot,
  Row: HomepageRow,
  Section: SectionContainer,
  Text,
};
